import type { ReactNode } from "react";
import { HomeSportIcon, isHomeIconSport } from "@/app/components/landing/v2/sport-icons";
import type { HubIconKey } from "@/lib/conditions/hub/types";

/**
 * Line icons for the /conditions hub (F4). Sport glyphs the homepage already draws come from
 * `HomeSportIcon`; the rest (off-piste, touring, the two foils, the "all" grid, areas, search and
 * the analysis tools, with phone variants where the F4 phone mockup draws its own) are drawn here
 * on the same 24px grid with a currentColor stroke, plus the 16px arrows, chevrons, close and
 * tick. The hub's single icon module. Always decorative (aria-hidden): the text beside each icon
 * names it. No hooks, so server and client components can both use it.
 */

export type HubGlyph =
  | HubIconKey
  | "all"
  | "pin"
  | "coast"
  | "mountain"
  | "trophy"
  | "trophy-phone"
  | "search"
  | "air"
  | "water"
  | "compare"
  | "air-phone"
  | "water-phone"
  | "compare-phone";

const FOIL_BOARD = "M3 8.5C3 7.4 7 6.5 12 6.5S21 7.4 21 8.5 17 10.5 12 10.5 3 9.6 3 8.5Z";
const PEAK = "M2.5 19.5L9 8L12.5 13.5L15 10L21.5 19.5Z";

function wave(y: number): string {
  return `M3 ${y}C4.5 ${y} 4.5 ${y - 1} 6 ${y - 1}S7.5 ${y} 9 ${y} 10.5 ${y - 1} 12 ${y - 1} 13.5 ${y} 15 ${y} 16.5 ${y - 1} 18 ${y - 1} 19.5 ${y} 21 ${y}`;
}

const EXTRA_PATHS: Readonly<Record<string, ReactNode>> = {
  "off-piste": (
    <>
      <path d={PEAK} />
      <path d="M6.8 12L8.4 13L10.3 11.3" />
    </>
  ),
  touring: (
    <>
      <path d="M2.5 19.5L9.5 6.5L15 15.5" />
      <path d="M11.5 21L21.5 14.5" />
      <circle cx="19" cy="9" r="1.8" />
    </>
  ),
  foil: (
    <>
      <path d={FOIL_BOARD} />
      <path d="M12 10.5V17" />
      <path d="M7 17.5C9.5 16.6 14.5 16.6 17 17.5" />
    </>
  ),
  "downwind-foil": (
    <>
      <path d={FOIL_BOARD} />
      <path d="M12 10.5V17" />
      <path d="M7 17.5C9.5 16.6 14.5 16.6 17 17.5" />
      <path d="M14.5 2.5H21M17 4.5H21" />
    </>
  ),
  all: (
    <>
      <path d="M4 4H10V10H4Z" />
      <path d="M14 4H20V10H14Z" />
      <path d="M4 14H10V20H4Z" />
      <path d="M14 14H20V20H14Z" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" />
      <circle cx="12" cy="9.5" r="2.5" />
    </>
  ),
  coast: (
    <>
      <path d={wave(8)} />
      <path d={wave(13)} />
      <path d={wave(18)} />
    </>
  ),
  mountain: (
    <>
      <path d={PEAK} />
      <path d="M7.4 10.6L9 12L10.6 10.6" />
    </>
  ),
  trophy: (
    <>
      <path d="M7.5 3.5H16.5V9C16.5 11.5 14.5 13.5 12 13.5S7.5 11.5 7.5 9Z" />
      <path d="M7.5 5.5H4.5C4.5 8.5 5.8 10.2 8.1 10.6M16.5 5.5H19.5C19.5 8.5 18.2 10.2 15.9 10.6" />
      <path d="M12 13.5V17" />
      <path d="M8.5 20.5H15.5M9.5 20.5C9.5 18.4 10.6 17 12 17S14.5 18.4 14.5 20.5" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16L20.5 20.5" />
    </>
  ),
  air: (
    <>
      <path d="M3 8.5H14.5C16.2 8.5 17.5 7.2 17.5 5.5S16.2 2.5 14.5 2.5" />
      <path d="M3 12.5H19C20.4 12.5 21.5 13.6 21.5 15S20.4 17.5 19 17.5" />
      <path d="M3 16.5H11" />
    </>
  ),
  water: <path d="M12 3C12 3 5.5 10.5 5.5 14.5C5.5 18.1 8.4 21 12 21S18.5 18.1 18.5 14.5C18.5 10.5 12 3 12 3Z" />,
  compare: (
    <>
      <path d="M4 6.5H20M4 12H20M4 17.5H20" />
      <path d="M9 3.5V20.5" />
    </>
  ),
  "trophy-phone": (
    <>
      <path d="M7.5 3.5H16.5V9.5A4.5 4.5 0 0 1 7.5 9.5Z" />
      <path d="M7.5 5.5H4.5V7A3.5 3.5 0 0 0 8 10.5M16.5 5.5H19.5V7A3.5 3.5 0 0 1 16 10.5" />
      <path d="M12 14V17.5" />
      <path d="M8.5 20.5H15.5M9.5 17.5H14.5V20.5H9.5Z" />
    </>
  ),
  "air-phone": (
    <>
      <path d="M3 8.5H13.5A2.5 2.5 0 1 0 11 6" />
      <path d="M3 12.5H18A2.5 2.5 0 1 1 15.5 15" />
      <path d="M3 16.5H9" />
    </>
  ),
  "water-phone": (
    <>
      <path d="M12 3.5C12 3.5 5.5 10.5 5.5 14.5A6.5 6.5 0 0 0 18.5 14.5C18.5 10.5 12 3.5 12 3.5Z" />
      <path d="M9 15A3 3 0 0 0 12 18" />
    </>
  ),
  "compare-phone": (
    <>
      <path d="M7.5 4L3.5 8L7.5 12" />
      <path d="M3.5 8H17" />
      <path d="M16.5 12L20.5 16L16.5 20" />
      <path d="M20.5 16H7" />
    </>
  ),
};

/** A decorative hub icon (sport, area, tool), or nothing for an unknown key. */
export function HubIcon({
  name,
  size = 24,
  strokeWidth = 1.7,
  className,
}: Readonly<{ name: HubGlyph; size?: number; strokeWidth?: number; className?: string }>) {
  const extra = EXTRA_PATHS[name];
  if (!extra) {
    if (!isHomeIconSport(name)) return null;
    return (
      <span aria-hidden="true" className={`inline-flex shrink-0 ${className ?? ""}`}>
        <HomeSportIcon sport={name} size={size} />
      </span>
    );
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-hub-icon={name}
      className={`shrink-0 ${className ?? ""}`}
    >
      {extra}
    </svg>
  );
}

/** Arrow right on a 16px grid (CTAs and header links). */
export function HubArrowIcon({ size = 16, className = "" }: Readonly<{ size?: number; className?: string }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false" className={`shrink-0 ${className}`}>
      <path d="M2.5 8H12.5M8.5 4L12.5 8L8.5 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** The arrow that nudges right on hover of a `group` parent (reduced motion: still). */
export function HubArrow({ size = 16, className = "" }: Readonly<{ size?: number; className?: string }>) {
  return (
    <HubArrowIcon
      size={size}
      className={`transition-transform duration-300 ease-[cubic-bezier(.22,1,.36,1)] group-hover:translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0 ${className}`}
    />
  );
}

/** A chevron on a 16px grid, pointing right (row ends) or down (accordions, selects). */
export function HubChevron({
  direction,
  size = 16,
  className = "",
}: Readonly<{ direction: "down" | "right"; size?: number; className?: string }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false" className={`shrink-0 ${className}`}>
      <path
        d={direction === "down" ? "M4 6L8 10L12 6" : "M6 3.5L10.5 8L6 12.5"}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Chevron right on a 16px grid (row ends). */
export function HubChevronIcon({ size = 16 }: Readonly<{ size?: number }>) {
  return <HubChevron direction="right" size={size} />;
}

/** Chevron down on a 16px grid (the phone sport toggle). */
export function HubChevronDownIcon({ size = 16 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false" className="shrink-0">
      <path d="M3.5 6L8 10.5L12.5 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** A cross on a 16px grid (clear and close buttons). */
export function HubCloseIcon({ size = 16 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false" className="shrink-0">
      <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

/** A tick on a 16px grid (the selected sheet option). */
export function HubTickIcon({ size = 18 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false" className="shrink-0">
      <path d="M3 8.5L6.5 12L13 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
